import React, { useContext, useEffect, useState } from 'react';
import { Mail, Check, Copy, Loader2, Share2 } from 'lucide-react';
import { SiWhatsapp, SiGmail, SiFacebook, SiLinkedin, SiX } from 'react-icons/si';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { ArcaContext } from '../../context/ArcaContext.context';
import { useIsMobile } from '../../hooks/useMediaQuery';
import useReferralShare from '../../hooks/useReferralShare';
import getMyReferralCode from '../../api/referrals/getMyReferralCode';
import { ReferralCodeI } from '../../api/referrals/interfaces/Referrals';
import { analytics, ANALYTICS_EVENTS } from '../../services/analytics';
import { UserPlanStatus } from '../../api/users/interfaces/Users';
import { buildReferralMessage } from '../../utils/referralShare';

export interface InviteFriendModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	source: 'sidebar' | 'mobile_nav' | 'dashboard_cta';
	planStatus?: UserPlanStatus;
}

const InviteFriendModal: React.FC<InviteFriendModalProps> = ({
	open,
	onOpenChange,
	source,
	planStatus
}) => {
	const { token } = useContext(ArcaContext);
	const isMobile = useIsMobile();

	const [referralCode, setReferralCode] = useState<ReferralCodeI | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [messageText, setMessageText] = useState('');
	const [linkCopied, setLinkCopied] = useState(false);
	const [messageCopied, setMessageCopied] = useState(false);

	const loadReferralCode = async () => {
		if (!token) return;
		setLoading(true);
		setError(false);
		try {
			const data = await getMyReferralCode(token);
			setReferralCode(data);
			setMessageText(buildReferralMessage(data.url));
		} catch {
			setError(true);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (open) {
			analytics.track(ANALYTICS_EVENTS.REFERRAL_MODAL_OPENED, { source });
			loadReferralCode();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	useEffect(() => {
		if (!linkCopied) return;
		const timer = setTimeout(() => setLinkCopied(false), 2000);
		return () => clearTimeout(timer);
	}, [linkCopied]);

	useEffect(() => {
		if (!messageCopied) return;
		const timer = setTimeout(() => setMessageCopied(false), 2000);
		return () => clearTimeout(timer);
	}, [messageCopied]);

	const copyLink = async () => {
		if (!referralCode) return;
		await navigator.clipboard.writeText(referralCode.url);
		analytics.track(ANALYTICS_EVENTS.REFERRAL_LINK_COPIED);
		setLinkCopied(true);
	};

	const copyMessage = async () => {
		await navigator.clipboard.writeText(messageText);
		analytics.track(ANALYTICS_EVENTS.REFERRAL_MESSAGE_COPIED);
		setMessageCopied(true);
	};

	const {
		shareNative,
		shareWhatsApp,
		shareGmail,
		shareEmail,
		shareTwitter,
		shareLinkedIn,
		shareFacebook
	} = useReferralShare(referralCode?.url ?? '', messageText);

	const hasCompletedTransfer = planStatus === UserPlanStatus.ACTIVE_IN_ARCA;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-xl [--accent:150_60%_95%] [--accent-foreground:150_87%_28%]">
				<DialogHeader>
					<DialogTitle className="text-xl font-heading">
						{referralCode
							? `Invita a un amigo y llévate ${referralCode.reward_snapshot.display_name}`
							: 'Invita a un amigo'}
					</DialogTitle>
				</DialogHeader>

				{loading && (
					<div className="flex justify-center py-8">
						<Loader2 className="w-8 h-8 animate-spin text-primary" />
					</div>
				)}

				{error && !loading && (
					<div className="text-center py-6 space-y-3">
						<p className="text-sm text-muted-foreground">No hemos podido cargar tu link.</p>
						<Button variant="outline" size="sm" onClick={loadReferralCode}>
							Reintentar
						</Button>
					</div>
				)}

				{referralCode && !loading && (
					<div className="space-y-4">
						{/* Subtítulo dinámico según estado del referidor */}
						<p className="text-sm text-muted-foreground">
							{hasCompletedTransfer
								? `Consigue ${referralCode.reward_snapshot.display_name} por cada amigo que traiga su plan de pensiones antiguo a Arca Digital. En cuanto complete el traspaso, sumaremos una nueva aportación directamente a tu jubilación.`
								: `Puedes empezar a invitar ya. Para redimir tu premio necesitarás completar tu propio traspaso primero.`}
						</p>

						{/* URL del link */}
						<div>
							<label className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1 block">
								Tu link de invitación
							</label>
							<div className="flex gap-2">
								<input
									readOnly
									value={referralCode.url}
									onClick={(e) => (e.target as HTMLInputElement).select()}
									className="input-arca flex-1 text-sm cursor-pointer py-2"
								/>
								<TooltipProvider>
									<Tooltip>
										<TooltipTrigger asChild>
											<Button
												variant="outline"
												size="sm"
												onClick={copyLink}
												aria-label="Copiar link"
												className="shrink-0 transition-colors"
											>
												{linkCopied ? (
													<Check className="w-4 h-4 text-primary" />
												) : (
													<Copy className="w-4 h-4" />
												)}
											</Button>
										</TooltipTrigger>
										<TooltipContent>
											<p className="text-xs">{linkCopied ? 'Link copiado' : 'Copiar link'}</p>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							</div>
						</div>

						{/* Mensaje sugerido editable */}
						<div className="space-y-1.5">
							<label className="text-xs text-muted-foreground font-medium uppercase tracking-wide block">
								Mensaje sugerido (edítalo si quieres)
							</label>
							<textarea
								value={messageText}
								onChange={(e) => setMessageText(e.target.value)}
								rows={4}
								className="input-arca w-full text-sm resize-none"
							/>
							<TooltipProvider>
								<Tooltip>
									<TooltipTrigger asChild>
										<Button
											variant="outline"
											size="sm"
											onClick={copyMessage}
											aria-label="Copiar mensaje"
											className="w-full transition-colors"
										>
											{messageCopied ? (
												<Check className="w-4 h-4 mr-2 text-primary" />
											) : (
												<Copy className="w-4 h-4 mr-2" />
											)}
											{messageCopied ? 'Mensaje copiado' : 'Copiar mensaje'}
										</Button>
									</TooltipTrigger>
									<TooltipContent>
										<p className="text-xs">
											{messageCopied ? 'Mensaje copiado' : 'Copiar mensaje'}
										</p>
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						</div>

						{/* Botones de compartir */}
						{isMobile ? (
							<Button className="btn-arca-primary w-full" onClick={shareNative}>
								<Share2 className="w-4 h-4 mr-2" />
								Compartir
							</Button>
						) : (
							<div className="space-y-2">
								<p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
									Compartir en
								</p>
								<div className="flex flex-wrap gap-2">
									<Button variant="outline" size="sm" onClick={shareWhatsApp}>
										<SiWhatsapp className="w-4 h-4 mr-2 text-green-600" />
										WhatsApp
									</Button>
									<Button variant="outline" size="sm" onClick={shareTwitter}>
										<SiX className="w-4 h-4 mr-2" />
										Twitter/X
									</Button>
									<TooltipProvider>
										<Tooltip>
											<TooltipTrigger asChild>
												<Button variant="outline" size="sm" onClick={shareLinkedIn}>
													<SiLinkedin className="w-4 h-4 mr-2 text-blue-600" />
													LinkedIn
												</Button>
											</TooltipTrigger>
											<TooltipContent>
												<p className="text-xs max-w-[200px]">
													LinkedIn no permite mensaje prefilled. Copia el texto antes si quieres
													añadirlo manualmente.
												</p>
											</TooltipContent>
										</Tooltip>
									</TooltipProvider>
									<Button variant="outline" size="sm" onClick={shareGmail}>
										<SiGmail className="w-4 h-4 mr-2 text-red-500" />
										Gmail
									</Button>
									<Button variant="outline" size="sm" onClick={shareEmail}>
										<Mail className="w-4 h-4 mr-2" />
										Otro correo
									</Button>
									<Button variant="outline" size="sm" onClick={shareFacebook}>
										<SiFacebook className="w-4 h-4 mr-2 text-blue-600" />
										Facebook
									</Button>
								</div>
							</div>
						)}
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default InviteFriendModal;
